import axios from 'axios';

// Configuração base da API
// Create React App usa process.env.REACT_APP_*
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5164/api';

// Criar instância do axios com configuração padrão
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token em todas as requisições
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar erros de autenticação
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado ou inválido
      clearToken();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ==================== Token Management ====================

export const getToken = () => {
  return localStorage.getItem('token');
};

export const setToken = (token) => {
  localStorage.setItem('token', token);
};

export const clearToken = () => {
  localStorage.removeItem('token');
};

// ==================== Auth API Calls ====================

/**
 * Login com email e senha
 * @param {string} email 
 * @param {string} password 
 * @returns {Promise<AuthResponse>}
 */
export const login = async (email, password) => {
  const response = await api.post('/auth/login', {
    email,
    password,
  });
  return response.data;
};

/**
 * Registro de novo usuário
 * @param {Object} userData - Dados do usuário
 * @param {string} userData.email
 * @param {string} userData.password
 * @param {string} userData.confirmPassword
 * @param {string} userData.name
 * @param {string} userData.professionalType - "Medico", "Fonoaudiologo", "Engenheiro"
 * @param {string} [userData.crm] - Obrigatório se Medico
 * @param {string} [userData.crmState] - Obrigatório se Medico
 * @param {string} [userData.crfa] - Obrigatório se Fonoaudiologo
 * @param {string} [userData.crfaRegion] - Obrigatório se Fonoaudiologo
 * @param {string} [userData.crea] - Obrigatório se Engenheiro
 * @param {string} [userData.creaState] - Obrigatório se Engenheiro
 * @returns {Promise<AuthResponse>}
 */
export const register = async (userData) => {
  const response = await api.post('/auth/register', userData);
  return response.data;
};

/**
 * Login com Google OAuth 2.0
 * @param {string} credential - Token de credencial do Google
 * @returns {Promise<AuthResponse>}
 */
export const googleLogin = async (credential) => {
  const response = await api.post('/auth/google', {
    googleToken: credential,
  });
  return response.data;
};

/**
 * Obter dados do usuário atual
 * @returns {Promise<UserDto>}
 */
export const getCurrentUser = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

/**
 * Logout (limpa token no servidor)
 * @returns {Promise<void>}
 */
export const logout = async () => {
  try {
    await api.post('/auth/logout');
  } catch (error) {
    // Ignorar erros de logout (já vamos limpar o token local)
    console.error('Erro ao fazer logout:', error);
  }
};

/**
 * Alterar senha do usuário
 * @param {string} currentPassword 
 * @param {string} newPassword 
 * @param {string} confirmNewPassword 
 * @returns {Promise<ApiResponse>}
 */
export const changePassword = async (currentPassword, newPassword, confirmNewPassword) => {
  const response = await api.post('/auth/change-password', {
    currentPassword,
    newPassword,
    confirmNewPassword,
  });
  return response.data;
};

// ==================== Helper Functions ====================

/**
 * Verifica se o usuário está autenticado
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  return !!getToken();
};

/**
 * Decodifica o JWT token (sem validação, apenas leitura)
 * @returns {Object|null} Payload do token ou null se inválido
 */
export const decodeToken = () => {
  const token = getToken();
  if (!token) return null;

  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Erro ao decodificar token:', error);
    return null;
  }
};

/**
 * Verifica se o token está expirado
 * @returns {boolean}
 */
export const isTokenExpired = () => {
  const decoded = decodeToken();
  if (!decoded || !decoded.exp) return true;

  const currentTime = Date.now() / 1000;
  return decoded.exp < currentTime;
};

/**
 * Atualizar perfil do usuário
 * @param {Object} profileData - Dados do perfil
 * @returns {Promise<UserDto>}
 */
export const updateProfile = async (profileData) => {
  const response = await api.put('/users/me/profile', profileData);
  return response.data;
};

export default api;
